import { memo, useDeferredValue, useMemo, useState } from "react";
import { EmptyPlaceholder } from "../../components/EmptyPlaceholder";
import { LoadingContainer } from "../../components/Loading";
import { useAppData } from "../../hooks";
import { IDocument } from "../../interfaces";
import { documentApi } from "../../services";
import { useAppSelector } from "../../store";
import { DocumentReaderToolbar } from "./components/DocumentReaderToolbar";
import { DocumentTextReader } from "./components/DocumentTextReader";
import { findChunks } from "./helper";
import { useStyles } from "./styles";

export type DocumentReaderAppProps = {
  doc: IDocument;
  textBlock?: string;
  onClose?: () => void;
};

export const DocumentReaderApp = memo(({ doc, onClose, textBlock }: DocumentReaderAppProps) => {
  const styles = useStyles();

  const [searchText, setSearchText] = useState<string>("");
  const searchQuery = useDeferredValue(searchText);
  const [activeMark, setActiveMark] = useState(0);

  const selectedAppId: string = useAppSelector((state) => state.apps.selectedApp);
  const [workspaceId] = useAppData<string>(selectedAppId, "workspaceId");
  const { data: documentText, isLoading } = documentApi.useGetDocumentTextQuery(
    { id: doc.doc_id, workspaceId },
    { skip: !workspaceId }
  );

  const textToHighlight = useMemo(
    () => documentText?.nodes.map((node) => node.text).join("\n\n") ?? "",
    [documentText?.nodes]
  );
  const searchWords = useMemo(() => [searchQuery], [searchQuery]);
  const highlightedText = useMemo(() => (textBlock ? [textBlock] : []), [textBlock]);

  const searchLength = useMemo(
    () => findChunks({ textToHighlight, searchWords }).length,
    [textToHighlight, searchWords]
  );

  return (
    <div className={styles.DocumentReaderContainer}>
      <DocumentReaderToolbar
        doc={doc}
        searchText={searchText}
        filteredSearchLength={searchLength}
        setSearchText={setSearchText}
        onClose={onClose}
        activeMark={activeMark}
        setActiveMark={setActiveMark}
      />
      <div className={styles.listContainer}>
        <LoadingContainer isLoading={isLoading} size="medium" label={"Downloading document ..."} labelPosition="below">
          {documentText?.nodes.length ? (
            <DocumentTextReader
              textToHighlight={textToHighlight}
              searchWords={searchWords}
              activeMark={activeMark}
              highlightedText={highlightedText}
            />
          ) : (
            <EmptyPlaceholder text={workspaceId ? "Unable to load document text" : "No workspace selected"} />
          )}
        </LoadingContainer>
      </div>
    </div>
  );
});
